// interceptors/error.interceptor.ts
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppUtilsService } from 'src/app/services/app-utils.service';
import { SharedDataService } from 'src/app/services/shared-data.service';

// Define error types
export enum TransactionErrorType {
  EXPIRED = 'ONBOARDING.TRANSACTION_EXPIRED',
  REJECTED = 'ONBOARDING.TRANSACTION_REJECTED',
  APPROVED = 'ONBOARDING.TRANSACTION_APPROVED',
  NEED_RESUME = 'ONBOARDING.TRANSACTION_NOT_BELONG_TO_USER',
}

interface ErrorResponse {
  error: string;
  data?: {
    redirect_url?: string;
    message?: string;
    [key: string]: any;
  };
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly WHITELIST_URLS = ['/auth', '/login', '/register'];

  constructor(
    private router: Router,
    private appUtils: AppUtilsService,
    private injector: Injector,
    private sharedDataService: SharedDataService,
    protected modalController: ModalController
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // ตรวจสอบว่า URL อยู่ใน whitelist หรือไม่
    const isWhitelisted = this.WHITELIST_URLS.some(url =>
      request.url.includes(url)
    );

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // ถ้าเป็น URL ที่อยู่ใน whitelist หรือเป็น 401 ให้ส่ง error ต่อไป
        if (isWhitelisted || error.status === HttpStatusCode.Unauthorized) {
          return throwError(() => error);
        }

        if (error.error instanceof ErrorEvent) {
          // Client-side error
          console.error('Client error:', error.error.message);
          return throwError(() => error);
        }

        // Handle server-side errors
        return this.handleServerError(error);
      })
    );
  }

  private handleServerError(error: HttpErrorResponse): Observable<never> {
    const errorResponse: ErrorResponse = error.error;
    const { error: errorCode, data } = errorResponse;

    // ตรวจสอบ Transaction Error ก่อน
    if (errorCode) {
      switch (errorCode) {
        case TransactionErrorType.EXPIRED:
          this.handleExpiredTransaction(data);
          return throwError(() => error);

        case TransactionErrorType.REJECTED:
          this.handleRejectedTransaction(error);
          return throwError(() => error);

        case TransactionErrorType.APPROVED:
          this.handleApprovedTransaction(data);
          return throwError(() => error);
      }
    }

    // ถ้าไม่ใช่ Transaction Error ให้จัดการตาม HTTP Status Code
    let errorMessage = '';

    switch (error.status) {
      // case HttpStatusCode.BadRequest:
      //   errorMessage = 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลอีกครั้ง';
      //   break;
      case HttpStatusCode.Forbidden:
        errorMessage = 'ไม่มีสิทธิ์เข้าถึงข้อมูล';
        break;
      case HttpStatusCode.NotFound:
        errorMessage = 'ไม่พบข้อมูลที่ต้องการ';
        break;
      case HttpStatusCode.TooManyRequests:
        errorMessage = 'มีการร้องขอข้อมูลมากเกินไป กรุณาลองใหม่ภายหลัง';
        break;
      case HttpStatusCode.InternalServerError:
        errorMessage = 'เกิดข้อผิดพลาดจากระบบ กรุณาลองใหม่ภายหลัง';
        break;
    }

    // แสดง error message
    if (errorMessage) {
      this.appUtils.showToast({
        message: errorMessage + ' ' + error.url,
        position: 'top',
      });
    }

    return throwError(() => error);
  }

  private async handleExpiredTransaction(data?: any) {
    this.sharedDataService.setData('topic', 'expired');
    await this.modalController.dismiss({}, 'error');
  }

  private async handleRejectedTransaction(error?: any) {
    // Set the rejection status in shared data
    this.sharedDataService.setData('topic', 'rejected');
    this.sharedDataService.setData('error-response', error);
    // Attempt to dismiss the modal
    await this.modalController.dismiss({}, 'error');
  }

  private async handleApprovedTransaction(data?: any): Promise<void> {
    await this.router.navigate(['/end']);
  }
}
